import get from 'lodash/get';
import { call, put, takeEvery, takeLatest, select } from 'redux-saga/effects';
import { Account } from '@commutifi-fe/interfaces';
import { clearAuthenticated, saveAuthenticated } from 'api/index';
import { resetTokenRefreshManager } from 'api/interceptors';
import { LoginResponse, loginUser, logoutUser } from 'api/modules/authentication/api';
import { AUTH_ERROR_TYPES } from '@commutifi-fe/constants';
import { getCurrentId } from '../accounts/selectors';
import * as actions from './actions';
import * as types from './types';
export function* login({
  auth,
  options,
  promise
}: actions.LoginAction<Account | {
  redirectPath?: string;
} | undefined>) {
  try {
    // Server call
    const res: LoginResponse | undefined = yield call(loginUser, auth);
    if (res?.redirectPath) {
      if (promise) yield call(promise.resolve, {
        redirectPath: res.redirectPath
      });
    }
    if (!res?.account) {
      throw new Error('We could not authenticate the user');
    }

    // For test purpose ONLY
    if (process.env.ENABLE_API_MOCK === 'true') {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const mockedAccountModule = require('api/__mocks__/modules/accounts/data');
      mockedAccountModule.setFakeAccountId(res.account.id);
    }
    yield put(actions.loginSuccess(res.account, options));
    if (promise) yield call(promise.resolve, res?.account);
  } catch (error: any) {
    const errorKey = get(error, 'data.error');
    const formattedError = {
      messageKey: AUTH_ERROR_TYPES[errorKey as keyof typeof AUTH_ERROR_TYPES] || AUTH_ERROR_TYPES[error?.status as keyof typeof AUTH_ERROR_TYPES],
      error
    };
    yield put(actions.loginFailure(formattedError));
    if (promise) yield call(promise.reject, formattedError);
  }
}
export function* loginSuccess({
  account,
  promise,
  options
}: actions.LoginSuccessAction) {
  try {
    if (!account.id) {
      throw new Error('Account id is missing in the login response');
    }
    // Reset the token refresh manager so future 401s can trigger refresh/unauthorized flow again
    resetTokenRefreshManager();
    yield call(saveAuthenticated, account.id, options);
    if (promise) yield call(promise.resolve, account);
  } catch (error) {
    if (promise) yield call(promise.reject, error);
  }
}
const timeoutCodes = new Set([4, 0]);
export function* logout({
  promise,
  payload
}: actions.LogoutAction) {
  try {
    // Get current account ID before clearing authentication
    const currentAccountId = yield select(getCurrentId);
    yield call(logoutUser);
    yield call(clearAuthenticated);
    yield put(actions.logoutSuccess({
      ...payload,
      currentAccountId
    }));
    if (promise) promise.resolve();
  } catch (e) {
    if (e && timeoutCodes.has(e?.readyState) && timeoutCodes.has(e?.status)) {
      const currentAccountId = yield select(getCurrentId);
      yield call(clearAuthenticated);
      yield put(actions.logoutSuccess({
        ...payload,
        currentAccountId
      }));
    }
    if (promise) promise.reject(e);
  }
}
export const authSagas = [takeEvery(types.LOGIN_REQUEST, login), takeEvery(types.LOGOUT, logout), takeLatest(types.LOGIN_SUCCESS, loginSuccess)];